(() => {
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const addMediaQueryListener = (query, listener) => {
    if (query.addEventListener) {
      query.addEventListener('change', listener);
    } else {
      query.addListener(listener);
    }
  };

  const removeMediaQueryListener = (query, listener) => {
    if (query.removeEventListener) {
      query.removeEventListener('change', listener);
    } else {
      query.removeListener(listener);
    }
  };

  class AristaCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.track = this.querySelector('[data-arista-carousel-track]');
      this.previousButton = this.querySelector('[data-arista-carousel-previous]');
      this.nextButton = this.querySelector('[data-arista-carousel-next]');
      this.pagination = this.querySelector('[data-arista-carousel-pagination]');
      this.pageByViewport = this.hasAttribute('data-arista-carousel-page');

      if (!this.track || !this.previousButton || !this.nextButton) return;

      this.initialized = true;
      this.handlePrevious = () => this.scroll(-1);
      this.handleNext = () => this.scroll(1);
      this.updateControls = () => {
        this.setControlState();
        this.setPaginationState();
      };
      this.handleResize = () => {
        this.renderPagination();
        this.updateControls();
      };

      this.previousButton.addEventListener('click', this.handlePrevious);
      this.nextButton.addEventListener('click', this.handleNext);
      this.track.addEventListener('scroll', this.updateControls, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.track);
      }

      this.renderPagination();
      this.updateControls();
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.previousButton.removeEventListener('click', this.handlePrevious);
      this.nextButton.removeEventListener('click', this.handleNext);
      this.track.removeEventListener('scroll', this.updateControls);
      this.resizeObserver?.disconnect();
      this.initialized = false;
    }

    scroll(direction) {
      if (this.pageByViewport) {
        const pageCount = this.getPaginationCount();
        const targetPage = Math.min(pageCount - 1, Math.max(0, this.getPaginationPage() + direction));
        const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);

        this.track.scrollTo({
          left: targetPage === pageCount - 1 ? maximumScroll : targetPage * this.track.clientWidth,
          behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
        });
        return;
      }

      const firstItem = this.track.firstElementChild;
      const gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
      const distance = firstItem ? firstItem.getBoundingClientRect().width + gap : this.track.clientWidth;

      this.track.scrollBy({
        left: direction * distance,
        behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
      });
    }

    setControlState() {
      const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const firstItem = this.track.firstElementChild;
      const lastItem = this.track.lastElementChild;
      const trackBounds = this.track.getBoundingClientRect();
      const isAtStart = firstItem
        ? firstItem.getBoundingClientRect().left >= trackBounds.left - 2
        : this.track.scrollLeft <= 2;
      const isAtEnd = lastItem
        ? lastItem.getBoundingClientRect().right <= trackBounds.right + 2
        : this.track.scrollLeft >= maximumScroll - 2;

      this.previousButton.disabled = isAtStart;
      this.nextButton.disabled = isAtEnd;
    }

    getPaginationCount() {
      if (!this.pagination) return 0;

      if (!this.track.clientWidth) return 1;

      const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);

      return Math.round(maximumScroll / this.track.clientWidth) + 1;
    }

    getPaginationPage() {
      const pageCount = this.getPaginationCount();
      if (pageCount < 2 || !this.track.clientWidth) return 0;

      return Math.min(pageCount - 1, Math.max(0, Math.round(this.track.scrollLeft / this.track.clientWidth)));
    }

    renderPagination() {
      if (!this.pagination) return;

      const pageCount = this.getPaginationCount();
      if (this.paginationButtons?.length === pageCount) return;

      this.pagination.replaceChildren();
      this.paginationButtons = [];
      this.pagination.hidden = pageCount < 2;

      for (let page = 0; page < pageCount; page += 1) {
        const button = document.createElement('button');
        button.className = 'arista-home-categories__pagination-dot';
        button.type = 'button';
        button.setAttribute('aria-label', `Show category page ${page + 1}`);
        button.setAttribute('aria-controls', this.track.id);
        button.addEventListener('click', () => {
          const maximumScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
          this.track.scrollTo({
            left: page === pageCount - 1 ? maximumScroll : Math.min(maximumScroll, page * this.track.clientWidth),
            behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
          });
        });
        this.pagination.append(button);
        this.paginationButtons.push(button);
      }
    }

    setPaginationState() {
      if (!this.paginationButtons?.length) return;

      const activePage = this.getPaginationPage();
      this.paginationButtons.forEach((button, page) => {
        const isActive = page === activePage;
        button.classList.toggle('is-active', isActive);
        if (isActive) {
          button.setAttribute('aria-current', 'page');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    }
  }

  class AristaHeroVideo extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.video = this.querySelector('[data-arista-hero-video]');
      this.source = this.querySelector('[data-arista-hero-source]');
      this.button = this.querySelector('[data-arista-hero-toggle]');
      this.label = this.querySelector('[data-arista-media-label]');
      this.status = this.querySelector('[data-arista-media-status]');

      if (!this.video || !this.button) return;

      this.initialized = true;
      this.userPaused = false;
      this.saveData = Boolean(navigator.connection?.saveData);
      this.handleToggle = () => this.togglePlayback();
      this.handlePlay = () => this.updateState(false);
      this.handlePause = () => this.updateState(false);
      this.handleMotionChange = (event) => {
        if (!this.saveData) this.applyMotionPreference(event.matches);
      };

      this.video.muted = true;
      this.button.addEventListener('click', this.handleToggle);
      this.video.addEventListener('play', this.handlePlay);
      this.video.addEventListener('pause', this.handlePause);
      addMediaQueryListener(reducedMotionQuery, this.handleMotionChange);

      if (this.saveData) {
        this.video.autoplay = false;
        this.video.removeAttribute('autoplay');
        this.video.pause();
        this.updateState(false, 'Background video is paused to save data.');
      } else {
        this.applyMotionPreference(reducedMotionQuery.matches);
      }
      this.updateState(false);
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.button.removeEventListener('click', this.handleToggle);
      this.video.removeEventListener('play', this.handlePlay);
      this.video.removeEventListener('pause', this.handlePause);
      removeMediaQueryListener(reducedMotionQuery, this.handleMotionChange);
      this.initialized = false;
    }

    applyMotionPreference(reduceMotion) {
      if (reduceMotion) {
        this.video.autoplay = false;
        this.video.removeAttribute('autoplay');
        this.video.pause();
        this.updateState(false, 'Background video is paused for reduced motion.');
        return;
      }

      this.video.autoplay = true;
      this.video.setAttribute('autoplay', '');
      if (!this.userPaused) this.playVideo();
    }

    togglePlayback() {
      if (this.video.paused) {
        this.userPaused = false;
        this.playVideo(true);
      } else {
        this.userPaused = true;
        this.video.pause();
        this.updateState(true);
      }
    }

    playVideo(announce = false) {
      this.ensureVideoSource();
      const playPromise = this.video.play();
      if (playPromise) {
        playPromise
          .then(() => this.updateState(announce))
          .catch(() => this.updateState(false, 'Background video could not start.'));
      }
    }

    ensureVideoSource() {
      if (!this.source?.dataset.src || this.source.src) return;
      this.source.src = this.source.dataset.src;
      this.source.removeAttribute('data-src');
      this.video.load();
    }

    updateState(announce, statusText) {
      const playing = !this.video.paused && !this.video.ended;
      const visibleLabel = playing ? 'Pause video' : 'Play video';
      const accessibleLabel = playing ? 'Pause background video' : 'Play background video';

      if (this.label) this.label.textContent = visibleLabel;
      this.button.dataset.playing = String(playing);
      this.button.setAttribute('aria-label', accessibleLabel);

      if (this.status && (announce || statusText)) {
        this.status.textContent = statusText || (playing ? 'Background video is playing.' : 'Background video is paused.');
      }
    }
  }

  class AristaWatchShop extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.items = [...this.querySelectorAll('[data-arista-watch-item]')]
        .map((element) => ({
          element,
          video: element.querySelector('[data-arista-watch-video]'),
          source: element.querySelector('[data-arista-watch-video] source[data-src]'),
          button: element.querySelector('[data-arista-watch-toggle]'),
          label: element.querySelector('[data-arista-media-label]'),
          status: element.querySelector('[data-arista-media-status]'),
          title: element.querySelector('.arista-home-watch-card__title')?.textContent.trim() || 'Arista video',
          userPaused: false,
        }))
        .filter((item) => item.video && item.button);

      if (!this.items.length) return;

      this.initialized = true;
      this.handleMotionChange = () => this.resetObserver();
      this.saveData = Boolean(navigator.connection?.saveData);
      this.visibility = new Map();

      this.items.forEach((item) => {
        item.video.muted = true;
        item.handleToggle = () => this.toggleItem(item);
        item.handlePlay = () => {
          this.pauseOthers(item.video);
          this.updateItemState(item, false);
        };
        item.handlePause = () => this.updateItemState(item, false);
        item.button.addEventListener('click', item.handleToggle);
        item.video.addEventListener('play', item.handlePlay);
        item.video.addEventListener('pause', item.handlePause);
        this.updateItemState(item, false);
      });

      addMediaQueryListener(reducedMotionQuery, this.handleMotionChange);
      this.resetObserver();
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.observer?.disconnect();
      this.items.forEach((item) => {
        item.video.pause();
        item.button.removeEventListener('click', item.handleToggle);
        item.video.removeEventListener('play', item.handlePlay);
        item.video.removeEventListener('pause', item.handlePause);
      });
      removeMediaQueryListener(reducedMotionQuery, this.handleMotionChange);
      this.initialized = false;
    }

    resetObserver() {
      this.observer?.disconnect();
      this.visibility.clear();
      this.items.forEach((item) => item.video.pause());

      if (reducedMotionQuery.matches || this.saveData || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const item = this.items.find((candidate) => candidate.element === entry.target);
            if (!item) return;
            this.visibility.set(item, entry.isIntersecting ? entry.intersectionRatio : 0);
          });

          const activeItem = this.items
            .filter((item) => !item.userPaused && (this.visibility.get(item) || 0) >= 0.55)
            .sort((first, second) => (this.visibility.get(second) || 0) - (this.visibility.get(first) || 0))[0];

          this.items.forEach((item) => {
            if (item === activeItem) {
              if (item.video.paused) this.playItem(item, false);
            } else {
              item.video.pause();
            }
          });
        },
        { threshold: [0, 0.01, 0.55, 1] }
      );

      this.items.forEach((item) => this.observer.observe(item.element));
    }

    toggleItem(item) {
      if (item.video.paused) {
        item.userPaused = false;
        this.playItem(item, true);
      } else {
        item.userPaused = true;
        item.video.pause();
        this.updateItemState(item, true);
      }
    }

    playItem(item, announce) {
      this.ensureVideoSource(item);
      this.pauseOthers(item.video);
      const playPromise = item.video.play();
      if (playPromise) {
        playPromise
          .then(() => this.updateItemState(item, announce))
          .catch(() => this.updateItemState(item, false, `${item.title} could not start.`));
      }
    }

    ensureVideoSource(item) {
      if (!item.source?.dataset.src || item.source.src) return;
      item.source.src = item.source.dataset.src;
      item.source.removeAttribute('data-src');
      item.video.load();
    }

    pauseOthers(activeVideo) {
      this.items.forEach((item) => {
        if (item.video !== activeVideo && !item.video.paused) item.video.pause();
      });
    }

    updateItemState(item, announce, statusText) {
      const playing = !item.video.paused && !item.video.ended;
      if (item.label) item.label.textContent = playing ? 'Pause video' : 'Play video';
      item.button.dataset.playing = String(playing);
      item.button.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${item.title}`);

      if (item.status && (announce || statusText)) {
        item.status.textContent = statusText || `${item.title} is ${playing ? 'playing' : 'paused'}.`;
      }
    }
  }

  if (!customElements.get('arista-carousel')) {
    customElements.define('arista-carousel', AristaCarousel);
  }

  if (!customElements.get('arista-hero-video')) {
    customElements.define('arista-hero-video', AristaHeroVideo);
  }

  if (!customElements.get('arista-watch-shop')) {
    customElements.define('arista-watch-shop', AristaWatchShop);
  }
})();
